<script setup>
import { ref, computed, toRefs, watch } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { US_STATES, COUNTRIES } from '@/constants'
import { 
  TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle,
  Combobox, ComboboxInput, ComboboxOptions, ComboboxOption, ComboboxButton
} from '@headlessui/vue'

const props = defineProps({
  editMode: { type: Boolean, default: true },
  location: { type: Object, default: {} },
  errors: { type: Array, default: [] }
})

const { editMode, location, errors } = toRefs(props)

const userStore = useUserStore()
const user = computed(() => userStore?.user || {})


const query = ref('')
const isDirty = ref(false)

let filteredStates = computed(() =>
  query.value === ''
    ? US_STATES
    : US_STATES.filter((state) =>
        state.name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, '')) ||
        state.abbreviation
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
)

let filteredCountry = computed(() =>
  query.value === ''
    ? COUNTRIES
    : COUNTRIES.filter((country) =>
        country.name
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, '')) ||
        country.abbreviation
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, ''))
      )
)

</script>
<template>
  <section>
    <div class="flex flex-col gap-y-4 text-xs font-normal text-gray-900">
      <label class="block">
        <span class="controller-label">Country</span>
        <template v-if="editMode">
          <Combobox v-model="location.country">
            <ComboboxInput
              @change="query = $event.target.value"
              class="input-controller font-normal py-2"
              :class="{'error': errors.includes('country')}"
              placeholder="Country"
            />
            <small v-if="errors.includes('country')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
            <TransitionRoot
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              @after-leave="query = ''"
            >
              <ComboboxOptions static class="border border-tint-1 rounded-lg">
                <ComboboxOption as="template" v-for="country, index in filteredCountry" :key="index" v-slot="{ active }" :value="country.abbreviation">
                  <li
                    class="flex items-center justify-between p-3 transition duration-300 hover:bg-blue-50 cursor-pointer group"
                    :class="{ 'bg-blue-50 rounded-lg': active }"
                  >
                    {{ `${country.name}, ${country.abbreviation}` }}
                  </li>
                </ComboboxOption>
              </ComboboxOptions>
            </TransitionRoot>
          </Combobox>
        </template>
        <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.country }" v-else>{{ location.country || '-' }}</p>
      </label>

      <label class="block">
        <span class="controller-label">Address Line 1</span>
        <template v-if="editMode">
          <input
            type="text"
            class="input-controller font-normal py-2"
            placeholder="Address Line 1"
            :class="{'error': errors.includes('address_1')}"
            v-model="location.address_1"
          />
          <small v-if="errors.includes('address_1')" class="controller-error-msg text-xs">
            <span>This field is required</span>
          </small>
        </template>
       
        <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_1 }" v-else>{{ location.address_1 || '-' }}</p>
      </label>
     
      <label class="block">
        <span class="controller-label">Address Line 2 <span class="text-gray-500">(Optional)</span></span>
        <template v-if="editMode">
          <input
            type="text"
            class="input-controller font-normal py-2"
            placeholder="Address Line 2 (Optional)"
            v-model="location.address_2"
          />
        </template>
        <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.address_2 }" v-else>{{ location.address_2 || '-' }}</p>
      </label>
     
      <div class="flex gap-x-4">
        <label class="block">
          <span class="controller-label">City</span>
          <template v-if="editMode">
            <input
              type="text"
              class="input-controller font-normal py-2"
              placeholder="City"
              :class="{'error': errors.includes('city')}"
              v-model="location.city"
            />
            <small v-if="errors.includes('city')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.city }" v-else>{{ location.city || '-' }}</p>
        </label>

        <label class="block">
          <span class="controller-label">State</span>
          <template v-if="editMode">
            <Combobox v-model="location.state">
              <ComboboxInput
                @change="query = $event.target.value"
                class="input-controller font-normal py-2"
                :class="{'error': errors.includes('state')}"
                placeholder="State"
              />
              <small v-if="errors.includes('state')" class="controller-error-msg text-xs">
                <span>This field is required</span>
              </small>
              <TransitionRoot
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                @after-leave="query = ''"
              >
                <ComboboxOptions static class="border border-tint-1 rounded-lg">
                  <ComboboxOption as="template" v-for="state, index in filteredStates" :key="index" v-slot="{ active }" :value="state.name">
                    <li
                      class="flex items-center justify-between p-3 transition duration-300 hover:bg-blue-50 cursor-pointer group"
                      :class="{ 'bg-blue-50 rounded-lg': active }"
                    >
                      {{ `${state.name}, ${state.abbreviation}` }}
                    </li>
                  </ComboboxOption>
                </ComboboxOptions>
              </TransitionRoot>
            
            </Combobox>
          </template>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.state }" v-else>{{ location.state || '-' }}</p>
        </label>

        <label class="block">
          <span class="controller-label">ZIP Code</span>
          <template v-if="editMode">
            <input
              type="text"
              class="input-controller font-normal py-2"
              placeholder="ZIP Code"
              :class="{'error': errors.includes('postal_code')}"
              v-model="location.postal_code"
            />
            <small v-if="errors.includes('postal_code')" class="controller-error-msg text-xs">
              <span>This field is required</span>
            </small>
          </template>
          <p class="h-9 leading-9 text-sm text-gray-900" :class="{ 'text-gray-400': !location.postal_code }" v-else>{{ location.postal_code || '-' }}</p>
        </label>
      </div> 
    </div>
  </section>
</template>